import { z } from 'zod';
import { Id } from './common';
import { BINARY, endpoint } from './endpoint';
import { lenientArray } from './lenient';

/** Only the first links of a message get a preview card. */
export const MAX_LINK_PREVIEWS = 3;
/** Longer addresses are linked but never fetched. */
export const MAX_LINK_PREVIEW_URL_CHARS = 2048;

/** What reads as a link in message text: http(s) up to whitespace, quotes or angle brackets. */
const LINK = /https?:\/\/[^\s<>"']+/g;
/** Sentence punctuation right after a link belongs to the sentence ("…/page." links "…/page"). */
const TRAILING = new Set([...'.,:;!?…。、，．！？：；']);
const OPENER: Record<string, string> = { ')': '(', ']': '[', '}': '{', '）': '（', '」': '「', '』': '『', '】': '【', '》': '《', '〉': '〈' };
const OPENERS = new Set(Object.values(OPENER));

/**
 * Ends the link at a closing bracket it did not open ("(https://a.example/x)" and
 * "「https://a.example/x」에서" link "…/x"), then drops trailing punctuation.
 */
function trimLinkEnd(candidate: string): string {
  const open: Record<string, number> = {};
  let end = candidate.length;
  for (let i = 0; i < candidate.length; i += 1) {
    const char = candidate[i]!;
    const opener = OPENER[char];
    if (opener === undefined) {
      if (OPENERS.has(char)) open[char] = (open[char] ?? 0) + 1;
    } else if (open[opener]) {
      open[opener] -= 1;
    } else {
      end = i;
      break;
    }
  }
  while (end > 0 && TRAILING.has(candidate[end - 1]!)) end -= 1;
  return candidate.slice(0, end);
}

/** A line of message text as plain runs and links. The app links exactly what the server previews. */
export function splitLinks(line: string): Array<{ text: string; link: boolean }> {
  const parts: Array<{ text: string; link: boolean }> = [];
  let last = 0;
  for (const match of line.matchAll(LINK)) {
    const link = trimLinkEnd(match[0]);
    // "https://" alone is not a link.
    if (link.length <= link.indexOf('//') + 2) continue;
    if (match.index > last) parts.push({ text: line.slice(last, match.index), link: false });
    parts.push({ text: link, link: true });
    last = match.index + link.length;
  }
  if (last < line.length) parts.push({ text: line.slice(last), link: false });
  return parts;
}

/**
 * The address a preview is fetched, cached and opened by: parsed by the URL standard
 * (IDN host in punycode, default port dropped) without its fragment, which never reaches
 * the server. Null for anything else than http(s), embedded credentials or an overlong address.
 */
export function normalizeLinkUrl(raw: string): string | null {
  // Length before parsing: parsing a long international host name is slow (a 10 000-character
  // one takes about 0.1 s), and the address without its fragment can only grow when normalised.
  if (raw.split('#', 1)[0]!.length > MAX_LINK_PREVIEW_URL_CHARS) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if ((url.protocol !== 'http:' && url.protocol !== 'https:') || url.username || url.password || !url.hostname) return null;
  url.hash = '';
  return url.href.length <= MAX_LINK_PREVIEW_URL_CHARS ? url.href : null;
}

/**
 * The links of a message that get a preview: the first distinct ones in the order they are
 * written, leaving out pages of `excludeOrigin` (the company server itself).
 */
export function linkPreviewUrls(text: string, options: { excludeOrigin?: string } = {}): string[] {
  const urls: string[] = [];
  for (const line of text.split('\n')) {
    for (const part of splitLinks(line)) {
      const url = part.link ? normalizeLinkUrl(part.text) : null;
      if (!url || urls.includes(url) || new URL(url).origin === options.excludeOrigin) continue;
      urls.push(url);
      if (urls.length === MAX_LINK_PREVIEWS) return urls;
    }
  }
  return urls;
}

/**
 * A card under a message for one of its links, made from what the company server read
 * from the linked page. Apps never contact the linked site for it: the thumbnail comes
 * from the company server as well.
 */
export const LinkPreview = z.object({
  id: Id,
  /** `normalizeLinkUrl` of the link in the text; the card opens it like the link itself. */
  url: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  siteName: z.string().nullable(),
  /** PNG or JPEG served by `linkPreviews.thumbnail`. */
  thumbnail: z.object({ width: z.number().int().positive(), height: z.number().int().positive() }).nullable(),
});
export type LinkPreview = z.infer<typeof LinkPreview>;

/** A preview a newer server describes differently is left out instead of failing the message. */
export const LinkPreviewList = lenientArray(LinkPreview);

export const linkPreviewsEndpoints = {
  /**
   * The author takes a preview off their own message for everyone; the link stays in the
   * text and the same link is not previewed again on that message. Returns what is left.
   */
  remove: endpoint({
    id: 'linkPreviews.remove',
    method: 'DELETE',
    path: '/api/v1/conversations/:conversationId/messages/:messageId/link-previews/:previewId',
    auth: 'session',
    params: z.object({ conversationId: Id, messageId: Id, previewId: Id }),
    response: z.object({ messageId: Id, linkPreviews: LinkPreviewList }),
  }),
  /** The preview image, to whoever may read the message (checked on every request). */
  thumbnail: endpoint({
    id: 'linkPreviews.thumbnail',
    method: 'GET',
    path: '/api/v1/link-previews/:previewId/thumbnail',
    auth: 'session',
    params: z.object({ previewId: Id }),
    response: BINARY,
  }),
};
